import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { canManagePayments } from "../accessControl";
import { callApi, formatMoney, getInlineLoader } from "../Helpers";
import { MdPayments, MdInfoOutline, MdCalendarToday, MdCreditCard } from "react-icons/md";

export default function View({ uuid }) {
    const appContext = useContext(AppContext);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    const [amount, setAmount] = useState('');
    
    // Core change-detection date engine states
    const defaultDateString = new Date().toISOString().split('T')[0];
    const [paymentDate, setPaymentDate] = useState(defaultDateString);
    const [dateIsChanged, setDateIsChanged] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!canManagePayments(appContext.currentUserData)) {
            appContext.tellError("Access denied.");
            return appContext.navTo({ item: "dashboard" });
        }
        if (uuid) getData();
    }, [uuid]);

    async function getData() {
        setReady(false);
        const res = await callApi('get-debt-data', { uuid });
        if (res.status === 1) setData(res.data);
        else appContext.tellError('Could not fetch data');
        setReady(true);
    }

    // Handler to watch if the date picker was interacted with
    function handleDateChange(val) {
        setPaymentDate(val);
        if (val !== defaultDateString) {
            setDateIsChanged(true);
        } else {
            setDateIsChanged(false);
        }
    }

    async function create() {
        if (!amount || Number(amount) <= 0) return appContext.tellError("Enter a valid amount");
        if (Number(amount) > Number(data?.balance)) return appContext.tellError("Amount exceeds remaining balance");
        if (!paymentDate) return appContext.tellError("Select a date");

        // Base structural request payload
        const payload = { 
            debtUuid: uuid, 
            amount: Number(amount)
        };

        // CONDITIONAL FLOW APPLIED HERE:
        // Only append dateCreated if the element state is explicitly flag-tripped by user interaction
        if (dateIsChanged) {
            // Parses string safely to UNIX seconds matching your backend create math structure precisely
            payload.dateCreated = Math.floor(new Date(`${paymentDate}T12:00:00`).getTime() / 1000);
        }

        setLoading(true);
        const res = await callApi('create-payment', payload);
        setLoading(false);

        if (res.status === 1) {
            appContext.tellMessage('Payment recorded successfully');
            appContext.getAllDebts();
            appContext.navBack();
        } else {
            appContext.tellError(res.msg);
        }
    }

    if (!ready) return <div className="container mSupportLoading">{getInlineLoader()}</div>;

    return (
        <div className="container">
            <div className="app-form-page">
                <div className="app-form-shell">
                    {/* Header */}
                    <div className="app-form-header">
                        <div>
                            <h6 className="app-form-title">Record Repayment</h6>
                            <p className="app-form-subtitle">Process a new payment for {data?.customerName || 'the customer'}.</p>
                        </div>
                        <div className="app-form-badge"><MdPayments size={18} /> Active Debt</div>
                    </div>

                    {/* Summary Section */}
                    <div className="app-form-section">
                        <div className="row g-3">
                            <div className="col-md-4"><div className="app-form-summary-card"><span>Customer</span><strong>{data?.customerName}</strong></div></div>
                            <div className="col-md-4"><div className="app-form-summary-card"><span>Motorcycle</span><strong>{data?.model}</strong></div></div>
                            <div className="col-md-4"><div className="app-form-summary-card border-primary"><span>Remaining Balance</span><strong>{formatMoney(data?.balance || 0)}</strong></div></div>
                        </div>
                    </div>

                    {/* Payment Form */}
                    <div className="app-form-section bg-light mt-3 p-4 rounded">
                        <div className="row">
                            <div className="col-md-6">
                                <label className="form-label"><MdCalendarToday size={16} /> Payment Date</label>
                                <input 
                                    type="date" 
                                    className="form-control" 
                                    value={paymentDate} 
                                    max={defaultDateString} 
                                    onChange={(e) => handleDateChange(e.target.value)} 
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label"><MdCreditCard size={16} /> Payment Amount</label>
                                <input type="number" className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                            </div>
                        </div>

                        <div className="mt-3 p-3 bg-white border rounded">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small"><MdInfoOutline size={14} /> New projected balance:</span>
                                <strong className="text-primary">{formatMoney(Math.max(Number(data?.balance || 0) - Number(amount || 0), 0))}</strong>
                            </div>
                        </div>

                        <button className="btn btn-primary btn-lg w-100 mt-4" onClick={create} disabled={loading}>
                            {loading ? "Processing..." : "RECORD PAYMENT"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}